import React, { useState, useEffect } from 'react';
import { serviceRequestService } from '../../services/service-request.service';
import { supportService } from '../../services/support.service';
import { ServiceRequest, SupportTicket } from '../../types';
import { Select } from '../../components/ui/Select';
import { Loading } from '../../components/ui/Loading';
import { formatDateTime } from '../../utils/formatters';
import { SERVICE_REQUEST_TYPES, SERVICE_REQUEST_STATUS } from '../../utils/constants';
import { toast } from 'react-toastify';

export const ServiceRequestsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'medical' | 'support'>('medical');
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [supportTickets, setSupportTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: ''
  });

  useEffect(() => {
    // Reset filters when switching tabs
    setFilters({ status: '', priority: '' });
  }, [activeTab]);

  useEffect(() => {
    loadData();

    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      loadData(true); // significant update: pass true to suppress loading spinner
    }, 5000);

    return () => clearInterval(interval);
  }, [activeTab, filters]);

  const loadData = async (isPolling = false) => {
    try {
      if (!isPolling) setIsLoading(true);
      if (activeTab === 'medical') {
        const response = await serviceRequestService.getAllRequests(filters);
        setRequests(response.items);
      } else {
        // Fetch all tickets then filter client-side if needed, since our simplistic API might not support all filters yet
        // But supportService.getAllTickets currently returns all.
        let tickets = await supportService.getAllTickets();

        // Manual client-side filtering until backend supports it
        if (filters.status) {
          tickets = tickets.filter(t => t.status === filters.status);
        }
        if (filters.priority) {
          tickets = tickets.filter(t => t.priority === filters.priority);
        }
        setSupportTickets(tickets);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      if (activeTab === 'medical') {
        await serviceRequestService.updateRequestStatus(id, status);
      } else {
        await supportService.updateTicketStatus(id, status);
      }
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (id: string, priority: string) => {
    try {
      if (activeTab === 'medical') {
        await serviceRequestService.updatePriority(id, priority);
        toast.success('Priority updated');
        loadData();
      } else {
        // Support tickets might not allow priority update via simplistic API yet, but let's assume readonly or add separate method if needed.
        // admin.routes.ts PATCH /support-tickets/:id only accepted status and resolution.
        // So priority update is NOT supported for support tickets in the current backend impl.
        toast.warning('Priority update not supported for support tickets');
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      // Service Request Statuses
      REQUESTED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',

      // Support Ticket Statuses
      OPEN: 'bg-blue-100 text-blue-800',
      RESOLVED: 'bg-green-100 text-green-800',
      CLOSED: 'bg-gray-100 text-gray-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadge = (priority: string) => {
    return priority === 'EMERGENCY'
      ? 'bg-red-100 text-red-800'
      : 'bg-gray-100 text-gray-800';
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Service Requests & Support</h1>
        <p className="text-gray-600">Manage member service requests and support tickets</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b border-gray-200">
        <button
          className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'medical'
            ? 'text-indigo-600 border-b-2 border-indigo-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => setActiveTab('medical')}
        >
          Medical Services
        </button>
        <button
          className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'support'
            ? 'text-indigo-600 border-b-2 border-indigo-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
          onClick={() => setActiveTab('support')}
        >
          Support Tickets
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            options={
              activeTab === 'medical'
                ? [
                  { value: '', label: 'All Statuses' },
                  { value: 'REQUESTED', label: 'Requested' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'COMPLETED', label: 'Completed' },
                  { value: 'CANCELLED', label: 'Cancelled' }
                ]
                : [
                  { value: '', label: 'All Statuses' },
                  { value: 'OPEN', label: 'Open' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'RESOLVED', label: 'Resolved' },
                  { value: 'CLOSED', label: 'Closed' }
                ]
            }
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          />
          <Select
            options={[
              { value: '', label: 'All Priorities' },
              { value: 'NORMAL', label: 'Normal' },
              { value: 'EMERGENCY', label: 'Emergency' }
            ]}
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loading size="lg" text="Loading..." />
        </div>
      ) : (
        /* Requests Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeTab === 'medical' ? (
            requests.map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {SERVICE_REQUEST_TYPES[request.type as keyof typeof SERVICE_REQUEST_TYPES]}
                    </h3>
                    <p className="text-sm text-gray-500">{formatDateTime(request.createdAt)}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(request.priority)}`}>
                    {request.priority}
                  </span>
                </div>

                <div className="mb-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(request.status)}`}>
                    {SERVICE_REQUEST_STATUS[request.status as keyof typeof SERVICE_REQUEST_STATUS]}
                  </span>
                </div>

                <div className="space-y-2">
                  <Select
                    options={[
                      { value: 'REQUESTED', label: 'Requested' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'CANCELLED', label: 'Cancelled' }
                    ]}
                    value={request.status}
                    onChange={(e) => handleStatusChange(request.id, e.target.value)}
                  />
                  <Select
                    options={[
                      { value: 'NORMAL', label: 'Normal' },
                      { value: 'EMERGENCY', label: 'Emergency' }
                    ]}
                    value={request.priority}
                    onChange={(e) => handlePriorityChange(request.id, e.target.value)}
                  />
                </div>
              </div>
            ))
          ) : (
            supportTickets.map((ticket) => (
              <div key={ticket.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-indigo-500">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900 line-clamp-1" title={ticket.subject}>
                      {ticket.subject}
                    </h3>
                    <p className="text-sm text-gray-500">{formatDateTime(ticket.createdAt)}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPriorityBadge(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>

                <div className="mb-3">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-4 line-clamp-2" title={ticket.description}>{ticket.description}</p>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Update Status</label>
                  <Select
                    options={[
                      { value: 'OPEN', label: 'Open' },
                      { value: 'IN_PROGRESS', label: 'In Progress' },
                      { value: 'RESOLVED', label: 'Resolved' },
                      { value: 'CLOSED', label: 'Closed' }
                    ]}
                    value={ticket.status}
                    onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Empty States */}
      {!isLoading && activeTab === 'medical' && requests.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No medical service requests found</p>
        </div>
      )}
      {!isLoading && activeTab === 'support' && supportTickets.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No support tickets found</p>
        </div>
      )}
    </div>
  );
};
